import { useState } from "react";
import {
  PlusIcon,
  XIcon,
} from "@heroicons/react/outline";
import {
  createBookingInvoices,
  generateBookingProforma,
  generateBookingInvoice,
  cancelBookingInvoice,
} from "../../../api/bookingsApi";
import ToastContainer from '../../../components/toastContainer';
import Toast from '../../../components/toast';

// ── Shared styles ──
const inputCls =
  "w-full bg-[#0d1424] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-pink-500/40 transition";

const labelCls =
  "block text-xs font-medium uppercase tracking-widest text-slate-400 mb-1.5";

const statusBadge = (value) => {
  if (!value) return <span className="text-slate-500">—</span>;
  const v = value.toLowerCase();
  const cls = v.includes("paid")
    ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
    : v.includes("overdue") || v.includes("cancel")
      ? "bg-red-500/10 text-red-400 border-red-500/20"
      : v.includes("pending") || v.includes("await")
        ? "bg-amber-500/10 text-amber-400 border-amber-500/20"
        : "bg-slate-500/10 text-slate-400 border-slate-500/20";
  return (
    <span className={`inline-block px-2.5 py-0.5 rounded-md text-xs font-medium border capitalize ${cls}`}>
      {value}
    </span>
  );
};

// ── Reusable Confirmation Modal ──
const ConfirmModal = ({ config, onConfirm, onCancel }) => {
  if (!config) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
      <div className="w-full max-w-sm bg-slate-900 border border-white/10 rounded-2xl shadow-2xl">
        <div className="px-6 py-5 text-center space-y-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto ${config.iconBg}`}>
            {config.icon}
          </div>
          <div>
            <h3 className="text-base font-semibold text-white">{config.title}</h3>
            <p className="text-xs text-slate-500 mt-1">{config.description}</p>
          </div>
        </div>
        <div className="flex gap-3 px-6 pb-5">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 text-sm rounded-xl text-slate-300 bg-slate-800 hover:bg-slate-700 transition"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 px-4 py-2.5 text-sm rounded-xl text-white font-medium transition ${config.confirmCls}`}
          >
            {config.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Tooltip icon button ──
const ActionButton = ({ onClick, disabled, loading, tooltip, colorCls, icon, spinColor }) => (
  <div className="relative group">
    <button
      onClick={onClick}
      disabled={disabled}
      className={`p-1.5 rounded-md transition disabled:opacity-40 disabled:cursor-not-allowed ${colorCls}`}
    >
      {loading ? (
        <svg className={`w-4 h-4 animate-spin ${spinColor}`} fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
      ) : icon}
    </button>
    <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-[10px] font-medium text-white bg-slate-700 rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10">
      {tooltip}
      <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-700" />
    </div>
  </div>
);

// ── SVG icons ──
const ProformaIcon = (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);
const InvoiceIcon = (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);
const CancelIcon = (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
  </svg>
);

// ── Create Instalment Modal ──
const CreateInstalmentModal = ({ bookingId, onClose, onSave }) => {
  const [form, setForm] = useState({ due_date: "", amount: "", vat: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError("");
  };

  const handleSubmit = async () => {
    if (!form.due_date || !form.amount) {
      setError("Due date and amount are required.");
      return;
    }
    try {
      setSaving(true);
      await onSave({ booking: bookingId, due_date: form.due_date, amount: form.amount, vat: form.vat || 0 });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.detail || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  const amount = parseFloat(form.amount) || 0;
  const vat = parseFloat(form.vat) || 0;
  const vatAmount = (amount * vat) / 100;
  const total = amount + vatAmount;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
      <div className="w-full max-w-sm bg-slate-900 border border-white/10 rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <h2 className="text-base font-semibold text-white">New Instalment</h2>
            <p className="text-xs text-slate-500 mt-0.5">Add a payment instalment to this booking</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition p-1 rounded-lg hover:bg-slate-800">
            <XIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className={labelCls}>Due Date <span className="text-pink-500">*</span></label>
            <input type="date" value={form.due_date} onChange={(e) => handleChange("due_date", e.target.value)} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Amount <span className="text-pink-500">*</span></label>
              <input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => handleChange("amount", e.target.value)} placeholder="0.00" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>VAT %</label>
              <input type="number" min="0" max="100" step="1" value={form.vat} onChange={(e) => handleChange("vat", e.target.value)} placeholder="0" className={inputCls} />
            </div>
          </div>
          {amount > 0 && (
            <div className="bg-slate-800/50 rounded-xl px-4 py-3 space-y-1.5">
              <div className="flex justify-between text-xs text-slate-400">
                <span>Amount</span><span className="text-white tabular-nums">{amount.toFixed(2)}</span>
              </div>
              {vat > 0 && (
                <div className="flex justify-between text-xs text-slate-400">
                  <span>VAT ({vat}%)</span><span className="text-white tabular-nums">{vatAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-semibold text-white pt-1.5 border-t border-white/10">
                <span>Total</span><span className="text-pink-400 tabular-nums">{total.toFixed(2)}</span>
              </div>
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition">Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            className={`px-5 py-2 text-sm rounded-lg text-white font-medium transition ${saving ? "bg-pink-400 cursor-not-allowed" : "bg-pink-600 hover:bg-pink-700"}`}>
            {saving ? "Saving…" : "Add Instalment"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Financial Tab ──
const FinancialTab = ({ booking, onRefresh }) => {
  const [showModal, setShowModal] = useState(false);
  const [instalments, setInstalments] = useState(booking.payment_instalments || []);
  const [toast, setToast] = useState(null);

  const [generatingProformaId, setGeneratingProformaId] = useState(null);
  const [generatingInvoiceId, setGeneratingInvoiceId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  // Single confirm state: { type: 'proforma' | 'invoice' | 'cancel', instId }
  const [confirm, setConfirm] = useState(null);

  const confirmConfigs = {
    proforma: {
      title: "Generate Proforma?",
      description: "This will generate a proforma document for this instalment. Continue?",
      confirmLabel: "Generate",
      confirmCls: "bg-emerald-600 hover:bg-emerald-700",
      iconBg: "bg-emerald-500/10 border border-emerald-500/20",
      icon: <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
    },
    invoice: {
      title: "Generate Invoice?",
      description: "This will generate an invoice document for this instalment. Continue?",
      confirmLabel: "Generate",
      confirmCls: "bg-pink-600 hover:bg-pink-700",
      iconBg: "bg-pink-500/10 border border-pink-500/20",
      icon: <svg className="w-6 h-6 text-pink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>,
    },
    cancel: {
      title: "Cancel Instalment?",
      description: "This will cancel this instalment. This action cannot be undone.",
      confirmLabel: "Cancel Instalment",
      confirmCls: "bg-red-600 hover:bg-red-700",
      iconBg: "bg-red-500/10 border border-red-500/20",
      icon: <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>,
    },
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    const { type, instId } = confirm;
    setConfirm(null);
    try {
      if (type === "proforma") {
        setGeneratingProformaId(instId);
        await generateBookingProforma(instId);
        setToast({ type: "success", message: "Proforma generated successfully." });
      } else if (type === "invoice") {
        setGeneratingInvoiceId(instId);
        await generateBookingInvoice(instId);
        setToast({ type: "success", message: "Invoice generated successfully." });
      } else if (type === "cancel") {
        setCancellingId(instId);
        await cancelBookingInvoice(instId);
        setToast({ type: "success", message: "Instalment cancelled." });
      }
      onRefresh?.();
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail || err?.detail || "Something went wrong.",
      });
    } finally {
      setGeneratingProformaId(null);
      setGeneratingInvoiceId(null);
      setCancellingId(null);
    }
  };

  const handleSave = async (payload) => {
    const newInstalment = await createBookingInvoices(payload);
    setInstalments((prev) => [...prev, newInstalment]);
    onRefresh?.();
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-white">Payment Instalments</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {instalments.length} instalment{instalments.length !== 1 ? "s" : ""} on this booking
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-1.5 text-xs font-medium text-pink-400 hover:text-pink-300 bg-pink-500/10 hover:bg-pink-500/15 border border-pink-500/20 px-3 py-1.5 rounded-lg transition"
        >
          <PlusIcon className="w-3.5 h-3.5" />
          New Instalment
        </button>
      </div>

      {/* Table */}
      <div className="bg-slate-900/60 rounded-2xl overflow-hidden">
        {instalments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-center">
            <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center mb-3">
              <PlusIcon className="w-5 h-5 text-slate-600" />
            </div>
            <p className="text-sm text-slate-500">No instalments yet</p>
            <p className="text-xs text-slate-600 mt-0.5">Click "New Instalment" to add one</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3.5 text-left">Due Date</th>
                  <th className="px-5 py-3.5 text-left">Document</th>
                  <th className="px-5 py-3.5 text-right">Amount</th>
                  <th className="px-5 py-3.5 text-right">VAT</th>
                  <th className="px-5 py-3.5 text-right">Total</th>
                  <th className="px-5 py-3.5 text-center">Status</th>
                  <th className="px-5 py-3.5 text-center">Invoice PDF</th>
                  <th className="px-5 py-3.5 text-center">Proforma PDF</th>
                  <th className="px-5 py-3.5 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {instalments.map((inst) => (
                  <tr key={inst.id} className="border-b border-white/5 last:border-none hover:bg-slate-800/30 transition">
                    <td className="px-5 py-3.5 text-sm text-slate-300 whitespace-nowrap">{inst.due_date ?? "—"}</td>
                    <td className="px-5 py-3.5 text-sm text-slate-300 whitespace-nowrap">{inst.document_type ?? "—"}</td>
                    <td className="px-5 py-3.5 text-sm text-slate-300 text-right tabular-nums whitespace-nowrap">{inst.amount} {inst.currency}</td>
                    <td className="px-5 py-3.5 text-sm text-slate-400 text-right tabular-nums">{inst.vat ?? "—"}</td>
                    <td className="px-5 py-3.5 text-sm text-white font-medium text-right tabular-nums whitespace-nowrap">{inst.total ?? "—"} {inst.currency}</td>
                    <td className="px-5 py-3.5 text-center">{statusBadge(inst.status)}</td>

                    {/* Invoice PDF */}
                    <td className="px-5 py-3.5 text-center">
                      {inst.provider_invoice_pdf_url ? (
                        <a href={inst.provider_invoice_pdf_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center justify-center p-1.5 rounded-md text-pink-400 hover:bg-pink-500/15 transition" title="View Invoice PDF">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </a>
                      ) : <span className="text-slate-600 text-xs">—</span>}
                    </td>

                    {/* Proforma PDF */}
                    <td className="px-5 py-3.5 text-center">
                      {inst.provider_proforma_pdf_url ? (
                        <a href={inst.provider_proforma_pdf_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center justify-center p-1.5 rounded-md text-emerald-400 hover:bg-emerald-500/15 transition" title="View Proforma PDF">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </a>
                      ) : <span className="text-slate-600 text-xs">—</span>}
                    </td>

                    {/* Actions */}
                    <td className="px-5 py-3.5 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <ActionButton
                          tooltip="Generate Proforma"
                          onClick={() => setConfirm({ type: "proforma", instId: inst.id })}
                          disabled={generatingProformaId === inst.id}
                          loading={generatingProformaId === inst.id}
                          colorCls="text-emerald-400 hover:bg-emerald-500/15"
                          spinColor="text-emerald-400"
                          icon={ProformaIcon}
                        />
                        <ActionButton
                          tooltip="Generate Invoice"
                          onClick={() => setConfirm({ type: "invoice", instId: inst.id })}
                          disabled={generatingInvoiceId === inst.id}
                          loading={generatingInvoiceId === inst.id}
                          colorCls="text-pink-400 hover:bg-pink-500/15"
                          spinColor="text-pink-400"
                          icon={InvoiceIcon}
                        />
                        <ActionButton
                          tooltip="Cancel Instalment"
                          onClick={() => setConfirm({ type: "cancel", instId: inst.id })}
                          disabled={cancellingId === inst.id || inst.status === "cancelled"}
                          loading={cancellingId === inst.id}
                          colorCls="text-red-400 hover:bg-red-500/15"
                          spinColor="text-red-400"
                          icon={CancelIcon}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Shared Confirmation Modal */}
      <ConfirmModal
        config={confirm ? confirmConfigs[confirm.type] : null}
        onConfirm={handleConfirm}
        onCancel={() => setConfirm(null)}
      />

      {showModal && (
        <CreateInstalmentModal
          bookingId={booking.id}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}

      <ToastContainer>
        {toast && (
          <div className="toast-enter">
            <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />
          </div>
        )}
      </ToastContainer>
    </div>
  );
};

export default FinancialTab;